import { ObjectAngle } from '../content-studio/canvas/surface-object-framing';
import { MOVES_3D, Move3d } from '../content-studio/canvas/surface-clip-recorder';
import { StudioBoard, StudioShot, resolveEngine } from './studio-contract';

/**
 * Surface products (tiles, flooring, wall covering) are photographed two ways,
 * and the two are made by different machinery:
 *
 *   installed — laid in a room: the scene is generated with the surface left
 *               plain and the visualizer maps the real texture onto it;
 *   object    — the tile itself: a packshot, a sample board, a flat lay, a
 *               macro of an edge. The real pieces are built in the Surface 3D
 *               Studio, captured, and the photo is made around that capture.
 *
 * A shot of the second kind needs its 3D capture before its photo; this file
 * says which capture, so a stale one is redone when the pose or camera changes.
 */

/** The arrangements the 3D studio can build (ids shared with surface-pose-layout). */
export const SURFACE_POSES = ['single', 'leaning', 'panel', 'grid', 'joint', 'stack', 'fan', 'flat-lay', 'in-hand'] as const;
export type SurfacePose = typeof SURFACE_POSES[number];

/** The object framing for a shot's camera. */
export function objectAngleFor(shot: Pick<StudioShot, 'camera' | 'pose'>): ObjectAngle {
    switch (shot.camera) {
        case 'top-down': return 'top-down';
        case 'macro':
        case 'detail': return shot.pose === 'joint' || shot.pose === 'grid' ? 'macro-joint' : 'macro-edge';
        case 'low': return 'low-angle';
        case 'three-quarter':
        case 'high':
        case 'wide': return 'three-quarter';
        default: return 'front';
    }
}

/** What a capture was made for; a different one means it has to be redone. */
export function captureKey(board: Pick<StudioBoard, 'format'>, shot: StudioShot): string {
    return JSON.stringify([shot.pose || 'single', objectAngleFor(shot), board.format, shot.productUuids[0] || '']);
}

/** The tile is the subject of this shot, so it is built in 3D first. */
export const isObjectShot = (shot: StudioShot): boolean => shot.surfaceUse === 'object';

/** An object shot whose 3D capture is missing or was made for another pose / camera. */
export function needsCapture(board: Pick<StudioBoard, 'format'>, shot: StudioShot): boolean {
    if (!isObjectShot(shot)) return false;
    return !(shot.framingRef?.source === 'studio3d' && shot.framingRef.key === captureKey(board, shot));
}

/**
 * A video shot of the tile itself, with nobody in it, is rendered in 3D: the
 * real pieces moving or the camera moving over them — exact in every frame,
 * and free. A hand or a person in the action needs a video model.
 */
export function clipIn3d(shot: StudioShot): boolean {
    return shot.kind === 'video' && isObjectShot(shot) && resolveEngine({ action: shot.action }) !== 'cinematic';
}

/** The 3D move for a shot: its own, else read from its camera move. */
export function move3dFor(shot: StudioShot): Move3d {
    if ((MOVES_3D as readonly string[]).includes(shot.move3d || '')) return shot.move3d as Move3d;
    switch (shot.motion) {
        case 'push-in': return 'push-in';
        case 'pull-out': return 'pull-out';
        case 'pan': return 'pan';
        case 'orbit': return 'orbit';
        case 'tilt':
        case 'crane-down': return 'tilt';
        case 'tracking': return 'glide';
        default: return 'static';
    }
}

/** Output size of a 3D clip for a board format (1080 on the short side). */
export function clipSize(format: string): { width: number; height: number } {
    const [w, h] = String(format || '1:1').split(':').map(Number);
    const r = w > 0 && h > 0 ? w / h : 1;
    return r >= 1 ? { width: Math.round(1080 * r), height: 1080 } : { width: 1080, height: Math.round(1080 / r) };
}
